import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '../utils/apiClient';
import { API_BASE } from '../utils/apiConfig';
import { useAuth } from '../hooks/useAuth';
import { normalizeRole } from '../utils/roles';

export const MonitoringContext = createContext(null);

export const MonitoringProvider = ({ children }) => {
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const isClient = normalizeRole(user?.role) === 'client';

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setDisabled(false);
      setLoaded(true);
      return;
    }
    try {
      setLoading(true);
      const resp = await api.get(`${API_BASE}me/monitoring-disabled`);
      setDisabled(resp?.data?.monitoring_disabled === true);
    } catch (e) {
      setDisabled(false);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setDisabled(false);
      setLoaded(true);
      return;
    }
    // Флаг нужен только клиенту: меню и роуты блокируются именно у него
    if (!isClient) {
      setDisabled(false);
      setLoaded(true);
      return;
    }
    // Сбрасываем loaded пока refresh не завершится, иначе MonitoringGate пустит на /dashboard со старым disabled=false
    setLoaded(false);
    refresh();
  }, [authLoading, isAuthenticated, isClient, user?.id, refresh]);

  const value = {
    disabled,
    loading,
    loaded,
    refresh,
    setDisabled
  };

  return (
    <MonitoringContext.Provider value={value}>
      {children}
    </MonitoringContext.Provider>
  );
};

export const useMonitoring = () => {
  const ctx = useContext(MonitoringContext);
  if (!ctx) {
    throw new Error('useMonitoring must be used within a MonitoringProvider');
  }
  return ctx;
};
